
"use client";

import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

interface AnimatedTextCycleProps {
  words: string[];
  interval?: number;
  className?: string;
}

export default function AnimatedTextCycle({
  words,
  interval = 5000,
  className = "",
}: AnimatedTextCycleProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [width, setWidth] = useState("auto");
  const measureRef = useRef<HTMLDivElement>(null);

  // Find max width among all words on mount
  useEffect(() => {
    if (measureRef.current) {
      let maxWordWidth = 0;
      Array.from(measureRef.current.children).forEach((element) => {
        const elementWidth = element.getBoundingClientRect().width;
        if (elementWidth > maxWordWidth) {
          maxWordWidth = elementWidth;
        }
      });
      // Set initial width to the current word's width
      if (measureRef.current.children[currentIndex]) {
        setWidth(`${measureRef.current.children[currentIndex].getBoundingClientRect().width}px`);
      }
    }

    // Handle resize events for responsive behavior
    const handleResize = () => {
      if (measureRef.current && measureRef.current.children[currentIndex]) {
        const newWidth = measureRef.current.children[currentIndex].getBoundingClientRect().width;
        setWidth(`${newWidth}px`);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get the width of the current word
  useEffect(() => {
    if (measureRef.current && measureRef.current.children[currentIndex]) {
      const newWidth = measureRef.current.children[currentIndex].getBoundingClientRect().width;
      setWidth(`${newWidth}px`);
    }
  }, [currentIndex]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % words.length);
    }, interval);

    return () => clearInterval(timer);
  }, [interval, words.length]);

  const containerVariants = {
    hidden: { 
      opacity: 0,
      y: 10,
      filter: "blur(4px)",
      transition: { 
        duration: 0.3,
        ease: "easeOut"
      }
    },
    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: {
        duration: 0.4,
        ease: "easeInOut"
      }
    },
    exit: { 
      opacity: 0,
      y: -10,
      filter: "blur(4px)",
      transition: { 
        duration: 0.3, 
        ease: "easeIn"
      }
    },
  };

  return (
    <motion.div 
      className="relative inline-flex mx-1 sm:mx-2 align-middle"
      animate={{ 
        width,
        transition: { 
          type: "spring",
          stiffness: 200,
          damping: 20 
        }
      }}
      style={{ minWidth: '50px' }} // Add minimum width for mobile
    >
      {/* Hidden measurement div */}
      <div 
        ref={measureRef} 
        aria-hidden="true"
        className="absolute opacity-0 pointer-events-none"
        style={{ visibility: "hidden" }}
      >
        {words.map((word, i) => (
          <span key={i} className={cn(className, "whitespace-nowrap")}>
            {word}
          </span>
        ))}
      </div>

      {/* Visible animated word */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={currentIndex}
          className={cn(
            className,
            "block whitespace-nowrap text-center",
            "relative before:content-[''] before:absolute before:inset-0",
            "before:bg-jade/10 before:blur-2xl before:transform before:scale-150",
            "before:-z-10"
          )}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {words[currentIndex]}
        </motion.span>
      </AnimatePresence>
    </motion.div>
  );
}
